// Nombre y subtítulo de una línea con promo: el regalo dice de qué promo sale y el descuento, cuál lo puso.
import type { ReactNode } from 'react';
import { SALE_PROMOTION_FALLBACK, SalePromotionTags } from '@/shared/patterns/SalePromotion';
import type { CartLine } from '../cartLines';

/** Promo de una línea; `format` arma la etiqueta con su nombre y `fallback` va si no se conoce. */
export interface PromotionLabelContext {
    promotionId: string;
    promotionVersion?: number;
    format: (name: string) => string;
    fallback: string;
    /** Con `true` la etiqueta dice si la promo solo aplica con crédito SureKeep. */
    withAudience?: boolean;
}

/** Lugares para las piezas de Promociones en un desglose; sin pieza se usa lo que trae la línea. */
export interface LineSlots {
    /** Nombre de un regalo; sin pieza, el del catálogo. */
    giftName?: (gift: { sku: string; name: string }) => ReactNode;
    /** Etiqueta con el nombre de la promo de la línea; sin pieza, su `fallback`. */
    promotionLabel?: (ctx: PromotionLabelContext) => ReactNode;
}

type LabelLine = Pick<
    CartLine,
    | 'sku'
    | 'name'
    | 'gift'
    | 'promotion'
    | 'promotions'
    | 'category'
    | 'qty'
    | 'description'
    | 'detail'
>;

/** Qué producto es: categoría, nombre de alta si lo hay y SKU; el kit agrega sus piezas. */
export function lineIdentity(line: LabelLine): string {
    const qty = line.category === 'BUNDLE' && line.qty > 1 ? ` · ×${String(line.qty)}` : '';
    const description =
        line.description && line.description !== line.name ? [line.description] : [];
    return [`${line.detail}${qty}`, ...description, line.sku].join(' · ');
}

function PromotionLabel({
    line,
    slots,
    format,
    fallback,
}: {
    line: LabelLine;
    slots: LineSlots;
    format: (name: string) => string;
    fallback: string;
    /** Con `true` la etiqueta dice si la promo solo aplica con crédito SureKeep. */
    withAudience?: boolean;
}) {
    if (line.promotion === undefined || !slots.promotionLabel) return <>{fallback}</>;
    return <>{slots.promotionLabel({ ...line.promotion, format, fallback })}</>;
}

/** Nombre de la línea; el de un regalo lo completa Promociones si el catálogo no lo conoce. */
export function LineName({ line, slots = {} }: { line: LabelLine; slots?: LineSlots }) {
    if (line.gift && slots.giftName)
        return <>{slots.giftName({ sku: line.sku, name: line.name })}</>;
    return <>{line.name}</>;
}

/** Etiqueta verde de la promo de venta de la línea: su nombre (de Promociones) y su código. */
export function LinePromotionBadge({
    line,
    slots = {},
    discounted = false,
    discountCode,
}: {
    line: LabelLine;
    slots?: LineSlots;
    discounted?: boolean;
    discountCode?: string;
}) {
    const refs = line.promotions ?? (line.promotion ? [line.promotion] : []);
    const named = discounted && refs.length > 0;
    if (line.gift || (!named && !discountCode)) return null;
    const names = named
        ? refs.map((ref) => (
              <PromotionLabel
                  key={ref.promotionId}
                  line={{ ...line, promotion: ref }}
                  slots={slots}
                  format={(n) => n}
                  fallback={SALE_PROMOTION_FALLBACK}
              />
          ))
        : [];
    return (
        <SalePromotionTags
            names={names}
            {...(discountCode ? { code: discountCode } : {})}
            className="min-w-0 shrink-0"
        />
    );
}

/** Subtítulo: el regalo dice su promo o su categoría; un descuento agrega su etiqueta verde. */
export function LineSubtitle({
    line,
    slots = {},
    discounted = false,
    discountCode,
}: {
    line: LabelLine;
    slots?: LineSlots;
    discounted?: boolean;
    discountCode?: string;
}) {
    const identity = lineIdentity(line);
    if (line.gift) {
        return (
            <PromotionLabel
                line={line}
                slots={slots}
                format={(name) => `Regalo de ${name} · ${line.sku}`}
                fallback={`Regalo · ${identity}`}
            />
        );
    }
    return (
        <span className="flex min-w-0 items-baseline gap-1.5">
            <span className="min-w-0 truncate">{identity}</span>
            <LinePromotionBadge
                line={line}
                slots={slots}
                discounted={discounted}
                {...(discountCode !== undefined && { discountCode })}
            />
        </span>
    );
}
